/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import Description from "App/model/description";
import RenderObject from "App/geometry/objects/object";
import Model from "App/model";
import Group from "App/model/group";
import Parts from "App/model/parts/parts";
import PartsPair from "App/model/parts/parts_pair";
import BoundingBox from "App/utils/bounding_box";
import {mat4} from "gl-matrix";
import {UPDATETYPES} from "App/render/webgl/renderables/batches/batch";
import IElement from "js_interface/src/elements/element_interface";

export enum EPartsTypes {
    volumes =     "volumes",
    lines =       "lines",
    texts =       "texts",
    pointClouds = "pointClouds",
    outlines =    "outlines",
    annotations = "annotations",
    entities =    "entities",
    trafo =       "trafo"
}

export
class RenderElement {

    protected _modelDescription: Description;
    protected _parsedMembers: any;
    protected _repetitionTransformation: mat4 = null;
    protected _parsedParts: any = {};
    protected _geometryObjects: RenderObject[] = [];
    protected _annotations: any = {};
    protected _groupRef: Group = null;
    protected _sceneApiElementRef: IElement = null;
    protected _typeName: string = "";
    protected _UID: number;

    protected visible: boolean = false;

    private _isSelected: boolean = false;

    public static TypeName: string = "Element";

    constructor(aModelDescription: Description) {
        this._modelDescription = aModelDescription;

        this._parsedMembers = {
            UID: null,
            id: -1,
            canCastShadow: true,
            keepCameraAbove: false
        };
    }

    public static create(aModelDescription: Description, aUID: number): RenderElement {
        let obj = new RenderElement(aModelDescription);
        obj._typeName = RenderElement.TypeName;
        obj._UID = aUID;
        return obj;
    }

    public getClassTypeName(): string {
        return this._typeName;
    }

    public setSceneAPIElementReference(aSceneAPIElement: IElement) {
        this._sceneApiElementRef = aSceneAPIElement;
    }

    public getSceneAPIElementReference(): IElement {
        return this._sceneApiElementRef;
    }

    public getIsReadyToRender(): boolean {
        for (let geometryObject of this._geometryObjects) {
            if (!geometryObject.getIsReadyToRender()) {
                return false;
            }
        }

        return true;
    }

    public getModel(): Model {
        return this._modelDescription.getRenderModel();
    }

    public getDownloadPriority(): number {
        return 0;
    }

    public getVisibility(): boolean {
        return this.visible;
    }

    public getGroup(): Group {
        return this._groupRef;
    }

    public getCanCastShadow(): boolean {
        return this._parsedMembers.canCastShadow;
    }

    public getType(): string {
        return this._parsedMembers.type;
    }

    /**
     * External 3rd party id, like cw3d gid.
     * @return {number|*}
     */
    public getParsedId(): number {
        return this._parsedMembers.id;
    }

    /**
     * @return {number|*}
     */
    public getUID(): number {
        return this._UID;
    }

    public getParsedUID(): number {
        return this._parsedMembers.UID;
    }

    public addGeometryObject(aGeometryObject: RenderObject) {
        this._geometryObjects.push(aGeometryObject);
    }

    public setGroup(aGroup: Group) {
        this._groupRef = aGroup;
    }

    public setSelected(aValue: boolean) {
        this._isSelected = aValue;
        for (let i = 0; i < this._geometryObjects.length; i++) {
            this._geometryObjects[i].setNeedUpdate([UPDATETYPES.SELECTION]);
        }
    }

    public getIsSelected() {
        return this._isSelected;
    }

    public getAnnotations() {
        return this._annotations;
    }

    public getAnnotationsOfType(aType: string) {
        let result = [];
        for (let a in this._annotations) {
            if (this._annotations[a]._data.type === aType) {
                switch (aType) {
                    case 'Metadata':
                        result.push(JSON.parse(this._annotations[a]._data.data));
                        break;

                    default:
                        result.push(this._annotations[a]._data.data);
                        break;

                }
            }
        }

        return result;
    }

    public getMetadata() {
        //      "type": "Metadata", "UID": 222 , "data": "{\"#1000\":\"Arêtier\",\"#1001\":\"\",\"#1002\":\"\",\"#1007\":\"\",\"#1008\":\"0\",\"DirX\":1,\"DirY\":0,\"DirZ\":0,\"Enable\":false,\"Meta Data\":[],\"PosX\":6.936121463775635,\"PosY\":3.6889665126800537,\"PosZ\":3.085501194000244,\"Text\":\"\"}"
        let result = [];

        for (let a in this._annotations) {
            if (this._annotations[a]._data.type === 'Metadata') {
                result.push(JSON.parse(this._annotations[a]._data.data));
            }
        }

        return result;
    }

    public setVisibility(aVisible: boolean) {
        // TODO: handle the changed drawstyle which caused that the visibility buffers for
        // outline geometry shapes needs to be reloaded to GPU
        let change = (this.visible !== aVisible);

        this.visible = aVisible;

        if (change) {
            for (let i = 0; i < this._geometryObjects.length; i++) {
                this._geometryObjects[i].setNeedUpdate([UPDATETYPES.VISIBILITY]);
            }
        }
    }

    public getGeometryObjects(): RenderObject[] {
        return this._geometryObjects;
    }

    public getBoundingBox(rBBox: BoundingBox) {
        for (let i = 0; i < this._geometryObjects.length; i++) {
            let object = this._geometryObjects[i];
            rBBox.extendByPoint(object.getBoundingBoxMin());
            rBBox.extendByPoint(object.getBoundingBoxMax());
        }
    }

    public getRepetitionTransformation(): mat4 {
        return this._repetitionTransformation;
    }

    public remove() {
        // nothing to do here now
    }

    public parse(aElementDescriptionJSON: any): boolean {
        this._parsedMembers.type = aElementDescriptionJSON.type;

        if (aElementDescriptionJSON.hasOwnProperty("id")) {
            this._parsedMembers.id = aElementDescriptionJSON.id;
        } else {
            this._parsedMembers.id = "-1";
        }

        if (!aElementDescriptionJSON.hasOwnProperty("UID")) {
            return false;
        }

        this._parsedMembers.UID = aElementDescriptionJSON.UID;

        if (aElementDescriptionJSON.hasOwnProperty("canCastShadow")) {
            this._parsedMembers.canCastShadow = aElementDescriptionJSON.canCastShadow;
        }

        let type = EPartsTypes.volumes;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._parsedParts[type] = new Parts();
            this._parsedParts[type].setValues(aElementDescriptionJSON[type]);
            this._parsedParts[type].setParsedShapesIDToElementMapping(this);
        }

        type = EPartsTypes.lines;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._parsedParts[type] = new Parts();
            this._parsedParts[type].setValues(aElementDescriptionJSON[type]);
            this._parsedParts[type].setParsedShapesIDToElementMapping(this);
        }

        type = EPartsTypes.texts;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._parsedParts[type] = new Parts();
            this._parsedParts[type].setValues(aElementDescriptionJSON[type]);
            this._parsedParts[type].setParsedShapesIDToElementMapping(this);
        }

        type = EPartsTypes.pointClouds;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._parsedParts[type] = new Parts();
            this._parsedParts[type].setValues(aElementDescriptionJSON[type]);
            this._parsedParts[type].setParsedShapesIDToElementMapping(this);
        }

        type = EPartsTypes.outlines;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._parsedParts[type] = new PartsPair();
            this._parsedParts[type].setValues(aElementDescriptionJSON[type]);
            this._parsedParts[type].setParsedShapesIDToElementMapping(this);
        }

        type = EPartsTypes.annotations;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            let annotationsArray = aElementDescriptionJSON[type];
            for (let i = 0; i < annotationsArray.length; i++) {
                let annotation = this._modelDescription.annotations.getAnnotationByUID(annotationsArray[i]);
                if (annotation) {
                    annotation.setElement(this);
                    this._annotations[annotation.getParsedUID()] = annotation;
                }
            }
        }

        type = EPartsTypes.trafo;
        if (aElementDescriptionJSON.hasOwnProperty(type)) {
            this._repetitionTransformation = aElementDescriptionJSON.trafo;
        }

        return true;
    }

    public getParts(aType: string): any {
        return this._parsedParts[aType];
    }

    public getJSONRepresentation(): any {
        let obj = {
            type: this._parsedMembers.type,
            id: this._parsedMembers.id,
            UID: this._parsedMembers.UID,
            canCastShadow: this._parsedMembers.canCastShadow
        };

        for (let member in this._parsedParts) {
            obj[member] = this._parsedParts[member].getValues();
        }

        return obj;
    }
}
